import React, { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";
import { Alert } from "../../components/Alert";
import { MissionDeduction, Repetition, RepetitionType } from "../../__generated__/graphql";
import { useTheme } from "../../hooks/use-theme";
import { BodyMediumText } from "../FormattedText";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import TextField from "../TextField";
import SelectField from "../SelectField";
import ListItem from "../../ui_atoms/ListItem";
import HStack from "../../ui_atoms/HStack";
import Gap from "../../ui_atoms/Gap";
import { useChainInputFocus } from "../../hooks/use-chain-input-focus";

export type MissionDeductionResult = Partial<MissionDeduction>;

export const validateDeductionData = (data: MissionDeduction) => {
    if (!data.description || data.description.length < 3) {
        return false;
    }
    return (data.amount || 0) > 0;
};

type MissionDeductionElementProps = {
    deduction: MissionDeduction;
    setDeduction: (deduction: MissionDeductionResult) => void;
    onRemove: () => void;
    repetition?: Repetition;
};

const MissionDeductionElement: React.FC<MissionDeductionElementProps> = ({
    deduction,
    setDeduction,
    onRemove,
    repetition,
}) => {
    const styles = useStyles();
    const { colors, gridSize } = useTheme();
    const i18n = useI18n();

    const amountValue = deduction.amount;
    const descriptionValue = deduction.description;

    const buildRepetitionOption = useCallback(
        (repetitionOption: RepetitionType) => {
            let label = i18n.t(`fees.repetition.${repetitionOption}`);
            if (repetition === Repetition.None && repetitionOption === RepetitionType.WorkedMonth) {
                label = i18n.t("fees.repetition.total");
            }
            return {
                label,
                value: repetitionOption,
            };
        },
        [i18n, repetition],
    );

    const repetitionsOptions = useMemo(() => {
        return [RepetitionType.WorkedMonth, RepetitionType.WorkedDay].map(buildRepetitionOption);
    }, [buildRepetitionOption]);

    const setAmount = useCallback(
        (newValue: string) => {
            const amount = parseFloat(newValue);
            if (isNaN(amount)) {
                setDeduction({
                    ...deduction,
                    amount: undefined,
                });
            } else {
                setDeduction({
                    ...deduction,
                    amount: amount * 100,
                });
            }
        },
        [deduction, setDeduction],
    );

    const setDescription = useCallback(
        (newValue: string) => {
            setDeduction({
                ...deduction,
                description: newValue,
            });
        },
        [deduction, setDeduction],
    );

    const { chainInputFocus } = useChainInputFocus();

    return (
        <VStack gap={0.5}>
            <HStack>
                <TextField
                    {...chainInputFocus(0)}
                    value={amountValue ? (amountValue / 100)?.toString() : ""}
                    onChangeText={setAmount}
                    error={amountValue ? isNaN(amountValue) : undefined}
                    showErrorText={false}
                    selectTextOnFocus
                    returnKeyType="done"
                    isDecimalNumber
                    style={styles.amount}
                    autoFocus={!amountValue}
                    left="CHF"
                    leftColor={colors.greyAccessible}
                    inputStyle={{ textAlign: "right" }}
                />
                <Gap gap={0.5} />
                <SelectField
                    value={deduction.repetition}
                    data={repetitionsOptions}
                    halfScreen
                    onSelect={(newRepetition) => {
                        setDeduction({
                            ...deduction,
                            repetition: newRepetition.value,
                        });
                    }}
                    style={styles.repetition}
                />
            </HStack>
            <TextField
                {...chainInputFocus(1)}
                enterKeyHint="next"
                submitBehavior="newline"
                label={i18n.t("screens.user.create_mission_deductions.description_field")}
                value={descriptionValue}
                onChangeText={setDescription}
                error={!!descriptionValue && descriptionValue.length < 3}
                showErrorText={false}
                multiline
                blurOnSubmit
            />
            <ListItem
                labels={[
                    {
                        text: i18n.t("screens.user.create_mission_deductions.remove_deduction"),
                        mode: "redCta",
                    },
                ]}
                onPress={onRemove}
                rightIcon="trash"
                rightActionMode="redCta"
                style={{ marginHorizontal: -(gridSize / 2) }}
            />
        </VStack>
    );
};

type MissionDeductionProps = {
    setDeductions: (fees: Array<MissionDeductionResult>) => void;
    deductions: Array<MissionDeduction>;
    repetition?: Repetition;
};

const MissionDeductions: React.FC<MissionDeductionProps> = ({
    setDeductions,
    deductions,
    repetition,
}) => {
    const { gridSize } = useTheme();
    const i18n = useI18n();
    const setDeduction = useCallback(
        (idx: number, deduction: MissionDeductionResult) => {
            const newDeductions = [...deductions] as Array<MissionDeductionResult>;
            newDeductions[idx] = deduction;
            setDeductions(newDeductions);
        },
        [setDeductions, deductions],
    );

    const addDeduction = useCallback(() => {
        const newDeductions = [...deductions] as Array<MissionDeductionResult>;
        newDeductions.push({
            description: "",
            amount: undefined,
            repetition: RepetitionType.WorkedDay,
        });
        setDeductions(newDeductions);
    }, [setDeductions, deductions]);

    const removeDeduction = useCallback(
        (idx: number) => {
            Alert.alert(
                i18n.t("screens.user.create_mission_deductions.remove_title"),
                i18n.t("screens.user.create_mission_deductions.remove_info"),
                [
                    {
                        text: i18n.t("cancel"),
                        style: "cancel",
                    },
                    {
                        text: i18n.t("screens.user.create_mission_deductions.do_remove"),
                        style: "destructive",
                        onPress: () => {
                            const newDeductions = [...deductions] as Array<MissionDeductionResult>;
                            newDeductions.splice(idx, 1);
                            setDeductions(newDeductions);
                        },
                    },
                    {},
                ],
            );
        },
        [deductions, setDeductions, i18n],
    );

    return (
        <VStack gap={1}>
            <BodyMediumText>{i18n.t("screens.user.create_mission_deductions.info")}</BodyMediumText>
            {deductions.map((deduction, idx) => {
                return (
                    <MissionDeductionElement
                        key={`${idx}`}
                        deduction={deduction}
                        setDeduction={(d) => setDeduction(idx, d)}
                        onRemove={() => removeDeduction(idx)}
                        repetition={repetition}
                    />
                );
            })}
            <ListItem
                labels={[
                    {
                        text: i18n.t("screens.user.create_mission_deductions.add_deduction"),
                        mode: "cta",
                    },
                ]}
                onPress={addDeduction}
                rightIcon="add-large"
                rightActionMode="action_simple"
                style={{ marginHorizontal: -(gridSize / 2), borderBottomWidth: 0 }}
            />
        </VStack>
    );
};

const useStyles = () => {
    const { colors, fonts, gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "flex-start",
                },
                text: {
                    ...fonts.bodyMedium,
                    color: colors.text,

                    padding: gridSize,
                },
                amount: {
                    flexGrow: 0,
                    flexShrink: 0,
                    width: 145,
                },
                repetition: {
                    flexGrow: 1,
                    width: 130,
                },
            }),
        [colors.text, fonts.bodyMedium, gridSize],
    );
};

export default MissionDeductions;
